/* Subtasks (Duncan 2026-10-30, the six): the customer sees the tree -- a parent with its rolled-up dates and % (Summit keeps
   them, server/subtasks.js), its subtasks indented under it. A parent shows when it, or any of its subtasks, is marked for
   the client; the summary counts bottom rows, so nothing is counted twice. */
import React, { useState, useEffect } from 'react';
import { getTasks, generateClientSchedule } from '../api';
import LoadingState from './LoadingState';
import { useTheme } from '../hooks/useTheme';
import { usePageSearch, matchesSearch, noMatchText } from '../lib/pageSearch';
import { rollUp, workingDays } from '../lib/rollup';

const ClientViewTab = ({ projectId, refreshSummary, workWeek = '5' }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportData, setExportData] = useState(null);

  const t = useTheme();
  // The top bar's search (lib/pageSearch): phase, task name, status. The table and the Schedule
  // Summary follow it, as they follow the client-visible filter.
  const search = usePageSearch('Search client tasks…');

  const fetchClientTasks = async () => {
    try {
      setLoading(true);
      const allTasks = await getTasks(projectId);
      const kidsOf = new Map();
      allTasks.forEach((x) => { if (x.parent_task_id != null) kidsOf.set(Number(x.parent_task_id), [...(kidsOf.get(Number(x.parent_task_id)) || []), x]); });
      const rows = [];
      allTasks.filter((x) => x.parent_task_id == null).forEach((x) => {
        const kids = (kidsOf.get(Number(x.id)) || [])
          .sort((a, b) => String(a.start_date || '9999').localeCompare(String(b.start_date || '9999')) || a.id - b.id);
        if (!kids.length) { if (x.client_visible) rows.push(x); return; }
        const shown = kids.filter((k) => k.client_visible);
        if (!x.client_visible && !shown.length) return;
        rows.push({ ...x, _parent: true, _shownKids: shown.length });
        shown.forEach((k) => rows.push({ ...k, _sub: true }));
      });
      setTasks(rows);
      setError(null);
    } catch (err) {
      setError('Failed to fetch client schedule');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientTasks();
  }, [projectId]);

  const handleExportSchedule = async () => {
    try {
      setExporting(true);
      const data = await generateClientSchedule({ project_id: projectId });
      setExportData(data);
    } catch (err) {
      setError('Failed to export client schedule');
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  // Task dates are days ('YYYY-MM-DD'). new Date() reads that as UTC midnight, which is the
  // evening before in Central, so parse the parts as a local date.
  const asLocalDay = (dateString) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateString || ''));
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(dateString);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return asLocalDay(dateString).toLocaleDateString('en-US');
  };

  // Working days on the job's week, as the Schedule counts them (2026-11-02: it counted calendar days).
  const calculateDuration = (startDate, finishDate) => {
    if (!startDate || !finishDate) return '-';
    const days = workingDays(startDate, finishDate, workWeek);
    return days > 0 ? `${days} days` : '-';
  };

  // Status pill colours from the theme tokens (so dark mode follows): Complete green like the
  // Schedule tab's pill, In Progress blue so it stands apart from Complete, everything else grey.
  const getStatusBadgeStyle = (status) => {
    switch (status?.toLowerCase()) {
      case 'complete':
      case 'completed':
      case 'closed':
        return { color: 'var(--success)', background: 'var(--success-soft)' };
      case 'in progress':
      case 'active':
        return { color: 'var(--type-labor)', background: 'color-mix(in oklch, var(--type-labor) 14%, transparent)' };
      case 'on hold':
        return { color: 'var(--warning)', background: 'var(--warning-soft)' };
      default:
        return { color: 'var(--text-3)', background: 'var(--bg-surface-2)' };
    }
  };

  const shownTasks = tasks.filter((task) => matchesSearch(search, task.building_area_phase, task.task_name, task.status || 'Not Started'));
  // The work itself: a parent's shown subtasks count, never the parent on top of them (a parent with none shown counts once).
  const countRows = shownTasks.filter((x) => !x._parent || !x._shownKids);
  const pctOf = (task) => (task._parent ? Math.round(Number(task.percent_complete) || 0) : (task.percent_complete || 0)); // a parent's is kept to 2 decimals

  if (loading) {
    return <LoadingState message="Loading client schedule…" />;
  }

  return (
    <div className="container">
      <div className="header-section">
        <h2>Client Schedule</h2>
        <button
          className="btn btn-primary"
          onClick={handleExportSchedule}
          disabled={exporting}
        >
          {exporting ? 'Exporting...' : 'Export Client Schedule'}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Export Preview */}
      {exportData && (
        <div className="card" style={{ marginBottom: '30px', backgroundColor: t.bgAlt }}>
          <h3>Export Preview</h3>
          <div style={{ fontSize: '15.5px', color: t.textSecondary }}>
            <p><strong>File Name:</strong> {exportData.filename}</p>
            <p><strong>Format:</strong> {exportData.format}</p>
            <p><strong>Tasks Included:</strong> {exportData.task_count}</p>
            <p><strong>Generated:</strong> {new Date(exportData.generated_at).toLocaleString('en-US')}</p>
            {exportData.download_url && (
              <a href={exportData.download_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary" style={{ marginTop: '10px' }}>
                Download File
              </a>
            )}
          </div>
        </div>
      )}

      {/* Client Schedule Table */}
      <div className="card">
        <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Phase</th>
              <th>Task Name</th>
              <th>Start Date</th>
              <th>Finish Date</th>
              <th>Duration</th>
              <th>% Complete</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                  No client-visible tasks available.
                </td>
              </tr>
            ) : shownTasks.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>{noMatchText(search).body}</td>
              </tr>
            ) : (
              shownTasks.map(task => (
                <tr key={task.id}>
                  <td>{task.building_area_phase || '-'}</td>
                  <td style={task._sub ? { paddingLeft: 28, color: 'var(--text-2)' } : task._parent ? { fontWeight: 600 } : undefined}>
                    {task._sub && <span aria-hidden style={{ display: 'inline-block', width: 9, height: 8, borderLeft: '1.5px solid var(--border-strong)', borderBottom: '1.5px solid var(--border-strong)', borderBottomLeftRadius: 3, margin: '0 7px 3px 0' }} />}
                    {task.task_name}
                  </td>
                  <td>{formatDate(task.start_date)}</td>
                  <td>{formatDate(task.finish_date)}</td>
                  <td>{calculateDuration(task.start_date, task.finish_date)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        style={{
                          flex: 1,
                          backgroundColor: t.cardBorder,
                          height: '20px',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          minWidth: '100px'
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: '#4CAF50',
                            height: '100%',
                            width: `${pctOf(task)}%`,
                            transition: 'width 0.3s'
                          }}
                        />
                      </div>
                      <span style={{ minWidth: '35px', textAlign: 'right', fontWeight: '500' }}>
                        {pctOf(task)}%
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className="badge" style={getStatusBadgeStyle(task.status)}>
                      {task.status || 'Not Started'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Summary Info */}
      {shownTasks.length > 0 && (
        <div className="card" style={{ marginTop: '20px' }}>
          <h3>Schedule Summary</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginTop: '16px' }}>
            <div style={{ padding: '12px', backgroundColor: t.bgAlt, borderRadius: '4px' }}>
              <p style={{ margin: 0, fontSize: '13.5px', color: t.textSecondary }}>Total Tasks</p>
              <p style={{ margin: 0, fontSize: '21.5px', fontWeight: 'bold' }}>{countRows.length}</p>
            </div>
            <div style={{ padding: '12px', backgroundColor: t.bgAlt, borderRadius: '4px' }}>
              <p style={{ margin: 0, fontSize: '13.5px', color: t.textSecondary }}>Completed</p>
              <p style={{ margin: 0, fontSize: '21.5px', fontWeight: 'bold' }}>
                {countRows.filter(t => ['complete', 'completed', 'closed'].includes(t.status?.toLowerCase())).length}
              </p>
            </div>
            <div style={{ padding: '12px', backgroundColor: t.bgAlt, borderRadius: '4px' }}>
              <p style={{ margin: 0, fontSize: '13.5px', color: t.textSecondary }}>In Progress</p>
              <p style={{ margin: 0, fontSize: '21.5px', fontWeight: 'bold' }}>
                {countRows.filter(t => t.status?.toLowerCase() === 'in progress' || t.status?.toLowerCase() === 'active').length}
              </p>
            </div>
            <div style={{ padding: '12px', backgroundColor: t.bgAlt, borderRadius: '4px' }}>
              <p style={{ margin: 0, fontSize: '13.5px', color: t.textSecondary }}>Not Started</p>
              <p style={{ margin: 0, fontSize: '21.5px', fontWeight: 'bold' }}>
                {countRows.filter(t => t.status?.toLowerCase() === 'not started' || t.status?.toLowerCase() === 'pending' || !t.status).length}
              </p>
            </div>
            <div style={{ padding: '12px', backgroundColor: t.bgAlt, borderRadius: '4px' }}>
              <p style={{ margin: 0, fontSize: '13.5px', color: t.textSecondary }}>Overall Progress</p>
              <p style={{ margin: 0, fontSize: '21.5px', fontWeight: 'bold' }}>
                {rollUp(countRows, workWeek).pct ?? 0}%
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientViewTab;
